// Purpose: status pill primitive. Comes in two shapes:
//   * Dot-indicator variant ("● ALL SYSTEMS OPERATIONAL" from the
//     sign-in mockup) — glowing dot + uppercase-tracked label.
//   * Plain-text tone variant for table / list status cells.
//
// The dot variant maps tone → status-color token; the dot itself uses
// `shadow-status-glow` which is `0 0 8px currentColor` so the glow
// inherits the tone color automatically.

import type { ReactNode } from 'react';
import { forwardRef } from 'react';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
export type StatusSize = 'sm' | 'md';

export interface StatusPillProps {
  /** Determines dot + muted-bg color. Default 'success'. */
  tone?: StatusTone;
  /** Shape. 'dot' = glowing dot + label. 'solid' = muted-bg pill. */
  shape?: 'dot' | 'solid';
  size?: StatusSize;
  children: ReactNode;
  className?: string;
}

const TONE_COLOR: Record<StatusTone, string> = {
  success: 'text-status-success',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
  info: 'text-status-info',
  neutral: 'text-fg-muted',
};

const TONE_BG: Record<StatusTone, string> = {
  success: 'bg-status-success-muted text-status-success',
  warning: 'bg-status-warning-muted text-status-warning',
  danger: 'bg-status-danger-muted text-status-danger',
  info: 'bg-status-info-muted text-status-info',
  neutral: 'bg-bg-tertiary text-fg-muted',
};

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { tone = 'success', shape = 'dot', size = 'md', children, className, ...rest },
  ref
) {
  if (shape === 'dot') {
    // The dot variant is the "mission ops" look — used in the nav, not
    // in tables. Text stays fg-muted; dot takes the tone color + glow.
    const textSize = size === 'sm' ? 'text-eyebrow' : 'text-label';
    return (
      <span
        ref={ref}
        className={[
          'inline-flex items-center gap-2',
          'text-fg-muted',
          textSize,
          'uppercase',
          className ?? '',
        ].join(' ')}
        {...rest}
      >
        <span
          aria-hidden
          className={[
            'inline-block rounded-full',
            size === 'sm' ? 'h-[5px] w-[5px]' : 'h-[6px] w-[6px]',
            TONE_COLOR[tone],
            'bg-current shadow-status-glow',
          ].join(' ')}
        />
        <span>{children}</span>
      </span>
    );
  }

  const padding = size === 'sm' ? 'px-2 py-0.5 text-caption' : 'px-3 py-1 text-body-s';
  return (
    <span
      ref={ref}
      className={[
        'inline-flex items-center rounded-sm font-semibold tracking-wide',
        'uppercase',
        padding,
        TONE_BG[tone],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
});
